import { ShieldCheck, Smartphone } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { addAuthenticator, ApiError, authenticatorOptions, type FactorProof, newRecoveryCodes, recoveryCodeOptions } from '../lib/api';
import { describeKeyError, signWithKey } from '../lib/webauthn';
import { QrCode, RecoveryCodes } from '../session/SecondFactor';
import { useSession } from '../session/session';
import { Button } from '../ui/Button';
import { Card } from '../ui/misc';
import { useToast } from '../ui/toast';

const field = 'h-9 w-full rounded-lg border border-line bg-panel px-2.5 text-[13.5px] outline-none focus:border-brand/50';
const refused = (error: unknown) => error instanceof ApiError
  ? error.status === 409 ? 'You already have an authenticator app.' : 'The confirming key touch or code was refused.' : describeKeyError(error);

/**
 * A person's own second factors (WRMM-134): every sign-in asks for one. Adding an authenticator app or new recovery
 * codes needs a touch of a key, or, for someone without one, a code: a stolen session alone can't change them.
 */
export function SecondFactors() {
  const { session, refresh } = useSession();
  const toast = useToast();
  const [adding, setAdding] = useState<{ secret: string; uri: string; confirm: Record<string, unknown> | null } | null>(null);
  const [newCode, setNewCode] = useState('');
  const [proofCode, setProofCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [askCode, setAskCode] = useState(false);
  // Someone whose key isn't to hand confirms with a code instead (the API takes either).
  const [useCode, setUseCode] = useState(false);
  const [busy, setBusy] = useState(false);
  const proof = async (confirm: Record<string, unknown> | null): Promise<FactorProof> => {
    if (!confirm || useCode) return { code: proofCode.trim() };
    try { return { confirmation: await signWithKey(confirm) }; }
    catch (error) { setUseCode(true); setAskCode(true); throw error; } // Offer the code next time.
  };
  const guard = async (work: () => Promise<void>, failure: string) => {
    setBusy(true);
    try { await work(); } catch (error) { toast({ tone: 'warning', title: failure, body: refused(error) }); } finally { setBusy(false); }
  };
  const finishAdding = (e: FormEvent) => {
    e.preventDefault();
    void guard(async () => {
      await addAuthenticator(newCode, await proof(adding!.confirm));
      setAdding(null); setNewCode(''); setProofCode(''); setUseCode(false); refresh();
      toast({ title: 'Authenticator app added', body: 'Its codes now sign you in.' });
    }, 'The authenticator app wasn’t added');
  };
  const makeCodes = () => void guard(async () => {
    const { confirm } = await recoveryCodeOptions();
    if ((!confirm || useCode) && !proofCode.trim()) { setAskCode(true); return; }
    setCodes((await newRecoveryCodes(await proof(confirm))).codes);
    setAskCode(false); setUseCode(false); setProofCode('');
  }, 'No new recovery codes were made');

  if (codes) return <Card className="px-4 py-4"><RecoveryCodes codes={codes} doneLabel="Done" onDone={() => { setCodes(null); refresh(); }} /></Card>;
  return (
    <Card className="divide-y divide-line">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <Smartphone className="size-4 text-ink-3" />
        <p className="min-w-0 flex-1"><span className="font-medium">Authenticator app</span>
          <span className="block text-[12.5px] text-ink-3">{session.authenticator ? 'Set up: its six-digit codes sign you in.' : 'Not set up.'}</span></p>
        {!session.authenticator && !adding && (
          <Button size="sm" disabled={busy} onClick={() => void guard(async () => { setAdding(await authenticatorOptions()); }, 'The authenticator app couldn’t be started')}>Add authenticator app</Button>
        )}
      </div>
      {adding && (
        <form onSubmit={finishAdding} className="grid gap-3 px-4 py-4 sm:grid-cols-[auto_minmax(0,1fr)]">
          <QrCode value={adding.uri} label="QR code for your authenticator app" />
          <div className="space-y-2 text-[13px]">
            <p className="text-ink-2">Scan it with your authenticator app, or enter <span className="break-all font-mono">{adding.secret}</span>, then enter the code it shows.</p>
            <label className="block font-medium">Code from the app
              <input className={`${field} mt-1`} inputMode="numeric" maxLength={6} value={newCode} onChange={e => setNewCode(e.target.value.replace(/\D/g, ''))} /></label>
            {(!adding.confirm || useCode) && <label className="block font-medium">One of your recovery codes, to confirm it’s you
              <input className={`${field} mt-1`} maxLength={40} value={proofCode} onChange={e => setProofCode(e.target.value)} /></label>}
            {adding.confirm && !useCode && <p className="text-ink-3">Adding it asks for a touch of your security key.{' '}
              <button type="button" className="text-brand underline" onClick={() => setUseCode(true)}>Use a recovery code instead</button></p>}
            <div className="flex gap-2">
              <Button type="submit" variant="primary" size="sm" disabled={busy || newCode.length !== 6 || ((!adding.confirm || useCode) && !proofCode.trim())}>Add</Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => setAdding(null)}>Cancel</Button>
            </div>
          </div>
        </form>
      )}
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <ShieldCheck className="size-4 text-ink-3" />
        <p className="min-w-0 flex-1"><span className="font-medium">Recovery codes</span>
          <span className={`block text-[12.5px] ${session.recoveryCodes <= 2 ? 'text-attention' : 'text-ink-3'}`}>
            {session.recoveryCodes ? `${session.recoveryCodes} unused; each signs you in once.` : 'None left. Make new ones.'}</span></p>
        {askCode && <input aria-label="A code from your authenticator app, or a recovery code" className={`${field} max-w-[200px]`} maxLength={40} value={proofCode}
          onChange={e => setProofCode(e.target.value)} placeholder="Authenticator or recovery code" />}
        <Button size="sm" disabled={busy} onClick={makeCodes}>{askCode ? 'Confirm' : 'New recovery codes'}</Button>
        {!askCode && <button type="button" className="text-[12.5px] text-brand underline" onClick={() => { setUseCode(true); setAskCode(true); }}>Use a code instead of a key</button>}
      </div>
      <p className="px-4 py-3 text-[12.5px] text-ink-3">Every sign-in asks for one of these, or one of your approval keys, after Google. If you lose them all, WITTCO resets them.</p>
    </Card>
  );
}
